/**
 * A function to create a log object for the 'devCenterMyAppsMoveAppToArchive' event (evid: 11415)
 *
 * Event description: User is approving the move of an app to archive in the move to archive modal
 *
 * @param {devCenterMyAppsMoveAppToArchiveParams} params - The parameters for this event
 * @property {string} last_update - the app last update date as it appears in My Apps page
 * @property {string} app_status - the app_status: DRAFT/SUBMITTED/PUBLISHED/DECLINED
 * @property {GUID} app_id - app id
 * @property {string} latest_version_status - the lastest app version status as it appears in My Apps page
 * @property {string} dev_ux_type - UX flavor: developer ,no-code
 *
 * @return {LogObject<devCenterMyAppsMoveAppToArchiveParams>} LogObject<devCenterMyAppsMoveAppToArchiveParams>
 */
export function devCenterMyAppsMoveAppToArchive(params) {
    return { evid: 11415, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterWebhookSentTriggered' event (evid: 1300)
 *
 * Event description: Dev Center - Webhook - Sent / Triggered
 *
 * @param {devCenterWebhookSentTriggeredParams} params - The parameters for this event
 * @property {GUID} instanceId - guid
 * @property {GUID} webhookId - guid
 * @property {string} url - url
 * @property {string} httpMethod - http method
 * @property {string} webhookEventType - webhook_event_type
 * @property {string} responseStatus - response_status
 * @property {string} topic - topic
 * @property {GUID} appId - app id
 * @property {string} originalTimestamp - originalTimestamp
 * @property {string} retryAttempt - retryAttempt
 * @property {string} statusCode - statusCode
 * @property {string} responseDuration - responseDuration
 * @property {string} kafkaEventId - unique identifier for each kafka attempt
 * @property {string} KafkaWebhookId - unique id to each webhook call (if there are 2 attempts - 2 row with this id)
 * @property {string} startHandlingTimestamp - start_handling_timestamp. when the webhook dispatcher started to work on the message
 * @return {LogObject<devCenterWebhookSentTriggeredParams>} LogObject<devCenterWebhookSentTriggeredParams>
 */
export function devCenterWebhookSentTriggered(params) {
    return { evid: 1300, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterAppActionsModalLoadedSrc96Evid183' event (evid: 183)
 *
 * Event description: Modal loaded in dev center 'my apps' or app's 'homepage'
 *
 * @param {devCenterAppActionsModalLoadedSrc96Evid183Params} params - The parameters for this event
 * @property {string} modal_name - name of the modal
 * @property {GUID} app_id - app_id
 * @property {string} dev_ux_type - ux flavor (developer, no-code)
 * @property {string} route - the page's route (from URL)
 * @return {LogObject<devCenterAppActionsModalLoadedSrc96Evid183Params>} LogObject<devCenterAppActionsModalLoadedSrc96Evid183Params>
 */
export function devCenterAppActionsModalLoadedSrc96Evid183(params) {
    return { evid: 183, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterMyAppsModalCtaClickSrc96Evid184' event (evid: 184)
 *
 * Event description: User clicked on CTA in a modal that is loaded from my apps
 *
 * @param {devCenterMyAppsModalCtaClickSrc96Evid184Params} params - The parameters for this event
 * @property {string} modal_name - name of the modal
 * @property {GUID} app_id - app_id
 * @property {string} dev_ux_type - ux flavor (developer, no-code)
 * @property {string} cta_name - name of the CTA
 * @property {string} to_url - the url of the destination
 * @property {string} route - the page's route (from URL)
 * @return {LogObject<devCenterMyAppsModalCtaClickSrc96Evid184Params>} LogObject<devCenterMyAppsModalCtaClickSrc96Evid184Params>
 */
export function devCenterMyAppsModalCtaClickSrc96Evid184(params) {
    return { evid: 184, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'appDashboardHomePageSectionLoadedWithErrorSrc96Evid185' event (evid: 185)
 *
 * Event description: When the homepage is being loaded with indication that the user have an error in his app building process.
 *
 * This is not an error on Wix side but an indication that the users app have something wrong' for example added a webhook without the proper permissions.
 *
 * @param {appDashboardHomePageSectionLoadedWithErrorSrc96Evid185Params} params - The parameters for this event
 * @property {string} error_name - The element with the error
 * @property {string} section_name - The section with the error
 * @property {string} error_description - error_description
 * @property {string} app_id - app_id
 * @return {LogObject<appDashboardHomePageSectionLoadedWithErrorSrc96Evid185Params>} LogObject<appDashboardHomePageSectionLoadedWithErrorSrc96Evid185Params>
 */
export function appDashboardHomePageSectionLoadedWithErrorSrc96Evid185(params) {
    return { evid: 185, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'appDashboardHomePageHoverOnAnErrorSrc96Evid186' event (evid: 186)
 *
 * Event description: When a user hover on the error indication in the app dasboard home page
 *
 * @param {appDashboardHomePageHoverOnAnErrorSrc96Evid186Params} params - The parameters for this event
 * @property {string} error_name - The element with the error
 * @property {string} section_name - The section with the error
 * @property {string} error_description - error_description
 * @property {string} app_id - app_id
 * @return {LogObject<appDashboardHomePageHoverOnAnErrorSrc96Evid186Params>} LogObject<appDashboardHomePageHoverOnAnErrorSrc96Evid186Params>
 */
export function appDashboardHomePageHoverOnAnErrorSrc96Evid186(params) {
    return { evid: 186, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewLegacyInstallerFallbackInitiatedSrc96Evid192' event (evid: 192)
 *
 * Event description: Client Side
 * The Consumer of the SDK launched the legacy (old) installer as a fallback mechanism.
 *
 *
 * @param {devCenterInstallerSdkNewLegacyInstallerFallbackInitiatedSrc96Evid192Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} hosting_platform - hosting_platform
 * @property {string} route - URL route of the install initiator
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @property {boolean} is_silent_install - Is the installer initiates a silent install flow
 * @property {boolean} avoid_post_installation - Is the post installation modal is skipped or not
 * @property {string} version_installed - the version of the app that is being installed on the site
 * @property {string} install_origin - location/platform/environment of the installation initiator
 * @property {string} installation_id - generated for each installation
 * @return {LogObject<devCenterInstallerSdkNewLegacyInstallerFallbackInitiatedSrc96Evid192Params>} LogObject<devCenterInstallerSdkNewLegacyInstallerFallbackInitiatedSrc96Evid192Params>
 */
export function devCenterInstallerSdkNewLegacyInstallerFallbackInitiatedSrc96Evid192(params) {
    return { evid: 192, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterReleaseVersionSuccessSrc96Evid194' event (evid: 194)
 *
 * Event description: Release Version Success/Fail from Server
 *
 * @param {devCenterReleaseVersionSuccessSrc96Evid194Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} origin - the page - modal - button that triggered the call
 * @property {string} comments_content - the content in comments
 * @property {string} from_version - previous version
 * @property {string} release_type - minor/major
 * @property {string} to_version - new version
 * @property {string} request_id - request_id
 * @property {string} release_status - fail/success
 * @return {LogObject<devCenterReleaseVersionSuccessSrc96Evid194Params>} LogObject<devCenterReleaseVersionSuccessSrc96Evid194Params>
 */
export function devCenterReleaseVersionSuccessSrc96Evid194(params) {
    return { evid: 194, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterReleaseVersionCallSentSrc96Evid195' event (evid: 195)
 *
 * Event description: Release Version Call Sent to Server
 *
 * @param {devCenterReleaseVersionCallSentSrc96Evid195Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} origin - the page - modal - button that triggered the call
 * @property {string} comments_content - the content in comments
 * @property {string} from_version - previous version
 * @property {string} release_type - minor/major
 * @property {string} to_version - new version
 * @return {LogObject<devCenterReleaseVersionCallSentSrc96Evid195Params>} LogObject<devCenterReleaseVersionCallSentSrc96Evid195Params>
 */
export function devCenterReleaseVersionCallSentSrc96Evid195(params) {
    return { evid: 195, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterAppSettingsWidgetCtaClickSrc96Evid196' event (evid: 196)
 *
 * Event description: App Settings Widget CTA Click
 *
 * @param {devCenterAppSettingsWidgetCtaClickSrc96Evid196Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} cta_name - cta_name
 * @property {string} section - section
 * @property {string} page_name - page_name
 * @return {LogObject<devCenterAppSettingsWidgetCtaClickSrc96Evid196Params>} LogObject<devCenterAppSettingsWidgetCtaClickSrc96Evid196Params>
 */
export function devCenterAppSettingsWidgetCtaClickSrc96Evid196(params) {
    return { evid: 196, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterVersionsModalLoadedSrc96Evid197' event (evid: 197)
 *
 * Event description: Release Version Modal Loaded
 *
 * @param {devCenterVersionsModalLoadedSrc96Evid197Params} params - The parameters for this event
 * @property {string} modal_name - name of the modal
 * @property {GUID} app_id - app_id
 * @property {string} release_type_options - minor, major, minor or major
 * @property {string} release_modal_type - cli/blocks/self hosted
 * @property {string} from_version - the current version as presented in the modal
 * @property {string} route - route (from page url)
 * @property {string} intent - intent (flow state)
 * @return {LogObject<devCenterVersionsModalLoadedSrc96Evid197Params>} LogObject<devCenterVersionsModalLoadedSrc96Evid197Params>
 */
export function devCenterVersionsModalLoadedSrc96Evid197(params) {
    return { evid: 197, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterVersionsModalCtaClickSrc96Evid198' event (evid: 198)
 *
 * Event description: Release Version Modal CTA Click (+ server call and response)
 *
 * @param {devCenterVersionsModalCtaClickSrc96Evid198Params} params - The parameters for this event
 * @property {string} modal_name - name of the modal
 * @property {GUID} app_id - app_id
 * @property {string} cta_name - click name
 * @property {string} release_modal_type - cli/blocks/self hosted
 * @property {string} release_type_apply - minor, major
 * @property {string} release_type_options - minor, major, minor or major
 * @property {string} route - route (from page url)
 * @property {string} intent - intent (flow state)
 * @property {string} comment_content - the full content of the comment (optional)
 * @property {string} request_id - request_id
 * @property {string} request_status - fail / success
 * @property {string} from_version - the current version as presented in the modal
 * @return {LogObject<devCenterVersionsModalCtaClickSrc96Evid198Params>} LogObject<devCenterVersionsModalCtaClickSrc96Evid198Params>
 */
export function devCenterVersionsModalCtaClickSrc96Evid198(params) {
    return { evid: 198, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkErrorModalLoadedSrc96Evid20' event (evid: 20)
 *
 * Event description: Error Modal Loaded in SDK installation flow
 *
 * @param {devCenterInstallerSdkErrorModalLoadedSrc96Evid20Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} uuid - uuid
 * @property {string} referral_info - referral_info
 * @property {string} install_origin - The location/platform/environment of the installation initiator
 * @property {string} load_origin - The location/platform/environment of where the modal loaded
 * @property {string} error_content_type - The content type of the error message
 * @property {string} error_reason - The technical reason
 * @return {LogObject<devCenterInstallerSdkErrorModalLoadedSrc96Evid20Params>} LogObject<devCenterInstallerSdkErrorModalLoadedSrc96Evid20Params>
 */
export function devCenterInstallerSdkErrorModalLoadedSrc96Evid20(params) {
    return { evid: 20, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkOAuthAppUrlStartSrc96Evid206' event (evid: 206)
 *
 * Event description: Client Side
 * Dev Center - Installer SDK - oAuth - appUrl Start (initiated)
 *
 *
 * @param {devCenterInstallerSdkOAuthAppUrlStartSrc96Evid206Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} instance_id - instance_id
 * @property {string} installation_id - generated for each installation
 * @property {string} url - oauth url
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @return {LogObject<devCenterInstallerSdkOAuthAppUrlStartSrc96Evid206Params>} LogObject<devCenterInstallerSdkOAuthAppUrlStartSrc96Evid206Params>
 */
export function devCenterInstallerSdkOAuthAppUrlStartSrc96Evid206(params) {
    return { evid: 206, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkOAuthAppUrlEndSrc96Evid207' event (evid: 207)
 *
 * Event description: Client Side
 * Dev Center - Installer SDK - oAuth - appUrl End
 *
 *
 * @param {devCenterInstallerSdkOAuthAppUrlEndSrc96Evid207Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} instance_id - instance_id
 * @property {string} installation_id - generated for each installation
 * @property {string} url - oauth url
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @return {LogObject<devCenterInstallerSdkOAuthAppUrlEndSrc96Evid207Params>} LogObject<devCenterInstallerSdkOAuthAppUrlEndSrc96Evid207Params>
 */
export function devCenterInstallerSdkOAuthAppUrlEndSrc96Evid207(params) {
    return { evid: 207, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkOAuthRedirectUrlStartSrc96Evid208' event (evid: 208)
 *
 * Event description: Client Side
 * Dev Center - Installer SDK - oAuth - redirectUrl Start
 *
 * @param {devCenterInstallerSdkOAuthRedirectUrlStartSrc96Evid208Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} instance_id - instance_id
 * @property {string} installation_id - generated for each installation
 * @property {string} url - oauth url
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @return {LogObject<devCenterInstallerSdkOAuthRedirectUrlStartSrc96Evid208Params>} LogObject<devCenterInstallerSdkOAuthRedirectUrlStartSrc96Evid208Params>
 */
export function devCenterInstallerSdkOAuthRedirectUrlStartSrc96Evid208(params) {
    return { evid: 208, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkOAuthRedirectUrlEndSrc96Evid209' event (evid: 209)
 *
 * Event description: Client Side
 * Dev Center - Installer SDK - oAuth - redirectUrl End
 *
 * @param {devCenterInstallerSdkOAuthRedirectUrlEndSrc96Evid209Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} instance_id - instance_id
 * @property {string} installation_id - generated for each installation
 * @property {string} url - oauth url
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @return {LogObject<devCenterInstallerSdkOAuthRedirectUrlEndSrc96Evid209Params>} LogObject<devCenterInstallerSdkOAuthRedirectUrlEndSrc96Evid209Params>
 */
export function devCenterInstallerSdkOAuthRedirectUrlEndSrc96Evid209(params) {
    return { evid: 209, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkInstallCallSentSrc96Evid21' event (evid: 21)
 *
 * Event description: Install Call Sent to Service
 *
 * @param {devCenterInstallerSdkInstallCallSentSrc96Evid21Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} account_id - for installs on account
 * @property {string} msid - msid
 * @property {string} hosting_platform - hosting_platform
 * @property {string} request_id - wait for server to get it
 * @property {string} route - route
 * @property {string} product - product
 * @property {string} install_origin - The location/platform/environment of the installation initiator
 * @return {LogObject<devCenterInstallerSdkInstallCallSentSrc96Evid21Params>} LogObject<devCenterInstallerSdkInstallCallSentSrc96Evid21Params>
 */
export function devCenterInstallerSdkInstallCallSentSrc96Evid21(params) {
    return { evid: 21, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkPostInstallationNavigationSrc96Evid210' event (evid: 210)
 *
 * Event description: Client Side
 * Dev Center - Installer SDK - Post Installation Navigation
 *
 * @param {devCenterInstallerSdkPostInstallationNavigationSrc96Evid210Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} instance_id - instance_id
 * @property {string} installation_id - generated for each installation
 * @property {string} destination - name of the destination
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @return {LogObject<devCenterInstallerSdkPostInstallationNavigationSrc96Evid210Params>} LogObject<devCenterInstallerSdkPostInstallationNavigationSrc96Evid210Params>
 */
export function devCenterInstallerSdkPostInstallationNavigationSrc96Evid210(params) {
    return { evid: 210, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterDistributeAppImpressionSrc96Evid212' event (evid: 212)
 *
 * Event description: Dev Center - App Status Widget Impression - App Distribution Page
 * Content viewed by dev
 *
 * @param {devCenterDistributeAppImpressionSrc96Evid212Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} section - section in the page
 * @property {string} page_name - page name
 * @property {string} row_name - the name of the row
 * @property {string} message_content - text of the message
 * @return {LogObject<devCenterDistributeAppImpressionSrc96Evid212Params>} LogObject<devCenterDistributeAppImpressionSrc96Evid212Params>
 */
export function devCenterDistributeAppImpressionSrc96Evid212(params) {
    return { evid: 212, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterAppChangesStatusImpressionHomePageSrc96Evid214' event (evid: 214)
 *
 * Event description: App Status Widget CTA Click
 *
 *
 * @param {devCenterAppChangesStatusImpressionHomePageSrc96Evid214Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} cta_name - cta_name
 * @property {string} section - section
 * @property {string} page_name - page_name
 * @return {LogObject<devCenterAppChangesStatusImpressionHomePageSrc96Evid214Params>} LogObject<devCenterAppChangesStatusImpressionHomePageSrc96Evid214Params>
 */
export function devCenterAppChangesStatusImpressionHomePageSrc96Evid214(params) {
    return { evid: 214, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterAppInfoModalLoadedSrc96Evid215' event (evid: 215)
 *
 * Event description: Dev Center - App Info Modal Loaded
 *
 * @param {devCenterAppInfoModalLoadedSrc96Evid215Params} params - The parameters for this event
 * @property {string} modal_name - name of the modal
 * @property {GUID} app_id - app_id
 * @property {string} route - route (from page url)
 * @return {LogObject<devCenterAppInfoModalLoadedSrc96Evid215Params>} LogObject<devCenterAppInfoModalLoadedSrc96Evid215Params>
 */
export function devCenterAppInfoModalLoadedSrc96Evid215(params) {
    return { evid: 215, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterAppInfoModalCtaClickSrc96Evid216' event (evid: 216)
 *
 * Event description: Dev Center - App Info Modal CTA Click
 *
 * @param {devCenterAppInfoModalCtaClickSrc96Evid216Params} params - The parameters for this event
 * @property {string} modal_name - name of the modal
 * @property {GUID} app_id - app_id
 * @property {string} cta_name - cta name
 * @property {string} route - route (from page url)
 * @return {LogObject<devCenterAppInfoModalCtaClickSrc96Evid216Params>} LogObject<devCenterAppInfoModalCtaClickSrc96Evid216Params>
 */
export function devCenterAppInfoModalCtaClickSrc96Evid216(params) {
    return { evid: 216, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkModalUnmountedSrc96Evid217' event (evid: 217)
 *
 * Event description: Client Side
 * Modal unmount
 * Exc. Error modal
 *
 *
 * @param {devCenterInstallerSdkModalUnmountedSrc96Evid217Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} hosting_platform - hosting_platform
 * @property {string} route - URL route of the install initiator
 * @property {string} install_origin - the url of the install origin
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {boolean} was_bulk_install_invoked - was bulk install invoked
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @property {string} modal_type - The type of the modal
 * @property {string} installation_id - installation_id
 * @return {LogObject<devCenterInstallerSdkModalUnmountedSrc96Evid217Params>} LogObject<devCenterInstallerSdkModalUnmountedSrc96Evid217Params>
 */
export function devCenterInstallerSdkModalUnmountedSrc96Evid217(params) {
    return { evid: 217, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterTestMyAppsWidgetCtaClickSrc96Evid218' event (evid: 218)
 *
 * Event description: In the developer center, user clicks on Test My App CTAs.
 *
 * @param {devCenterTestMyAppsWidgetCtaClickSrc96Evid218Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} page_name - origin page name
 * @property {string} route - The current URL route
 * @property {string} section - the section where the Call to Action (CTA) is located
 * @property {string} cta_name - The name of the cta click
 * @return {LogObject<devCenterTestMyAppsWidgetCtaClickSrc96Evid218Params>} LogObject<devCenterTestMyAppsWidgetCtaClickSrc96Evid218Params>
 */
export function devCenterTestMyAppsWidgetCtaClickSrc96Evid218(params) {
    return { evid: 218, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterTestMyAppModalLoadedSrc96Evid219' event (evid: 219)
 *
 * Event description: Modal loaded in dev center 'test my app' flow
 *
 * @param {devCenterTestMyAppModalLoadedSrc96Evid219Params} params - The parameters for this event
 * @property {string} modal_type - name /of the modal type
 * @property {GUID} app_id - app_id
 * @property {string} dev_ux_type - ux flavor (developer, no-code)
 * @property {string} route - the page's route (from URL)
 * @property {string} page_name - page name
 * @return {LogObject<devCenterTestMyAppModalLoadedSrc96Evid219Params>} LogObject<devCenterTestMyAppModalLoadedSrc96Evid219Params>
 */
export function devCenterTestMyAppModalLoadedSrc96Evid219(params) {
    return { evid: 219, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewErrorModalLoadedSrc96Evid22' event (evid: 22)
 *
 * Event description: Error Modal Loaded in SDK installation flow (new installer)
 *
 * @param {devCenterInstallerSdkNewErrorModalLoadedSrc96Evid22Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} uuid - uuid
 * @property {string} install_origin - The location/platform/environment of the installation initiator
 * @property {string} route - The route of the hosting url
 * @property {string} installation_id - generated for each installation
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @property {string} load_origin - The location/platform/environment of where the modal loaded
 * @property {string} request_id - request_id
 * @property {string} error_content_type - The content type of the error message
 * @property {string} error_reason - The technical reason
 * @return {LogObject<devCenterInstallerSdkNewErrorModalLoadedSrc96Evid22Params>} LogObject<devCenterInstallerSdkNewErrorModalLoadedSrc96Evid22Params>
 */
export function devCenterInstallerSdkNewErrorModalLoadedSrc96Evid22(params) {
    return { evid: 22, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterTestMyAppModalCtaClickSrc96Evid220' event (evid: 220)
 *
 * Event description: User click on a modal CTA in dev center 'test my app' flow
 *
 * @param {devCenterTestMyAppModalCtaClickSrc96Evid220Params} params - The parameters for this event
 * @property {string} modal_type - name /of the modal type
 * @property {GUID} app_id - app_id
 * @property {string} route - the page's route (from URL)
 * @property {string} cta_name - CTA name
 * @return {LogObject<devCenterTestMyAppModalCtaClickSrc96Evid220Params>} LogObject<devCenterTestMyAppModalCtaClickSrc96Evid220Params>
 */
export function devCenterTestMyAppModalCtaClickSrc96Evid220(params) {
    return { evid: 220, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewInstallCallSentSrc96Evid23' event (evid: 23)
 *
 * Event description: Install Call Sent to Service (new installer)
 *
 * @param {devCenterInstallerSdkNewInstallCallSentSrc96Evid23Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} hosting_platform - hosting_platform
 * @property {string} route - URL route of the install initiator
 * @property {string} installation_id - generated for each installation
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @property {string} install_origin - location/platform/environment of the installation initiator
 * @return {LogObject<devCenterInstallerSdkNewInstallCallSentSrc96Evid23Params>} LogObject<devCenterInstallerSdkNewInstallCallSentSrc96Evid23Params>
 */
export function devCenterInstallerSdkNewInstallCallSentSrc96Evid23(params) {
    return { evid: 23, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewInstallSuccessSrc96Evid24' event (evid: 24)
 *
 * Event description: Client Side
 * Install Process Completed Successfully (new installer)
 *
 *
 * @param {devCenterInstallerSdkNewInstallSuccessSrc96Evid24Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} hosting_platform - hosting_platform
 * @property {string} request_id - wait for server to get it
 * @property {string} route - URL route of the install initiator
 * @property {string} instance_id - instance_id
 * @property {string} installation_id - generated for each installation
 * @property {string} correlation_id - correlation_id is generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @property {string} install_origin - location/platform/environment of the installation initiator
 * @return {LogObject<devCenterInstallerSdkNewInstallSuccessSrc96Evid24Params>} LogObject<devCenterInstallerSdkNewInstallSuccessSrc96Evid24Params>
 */
export function devCenterInstallerSdkNewInstallSuccessSrc96Evid24(params) {
    return { evid: 24, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewInstallFailedSrc96Evid25' event (evid: 25)
 *
 * Event description: Client Side
 * Install Process Failed (new installer)
 * Server response error
 *
 * @param {devCenterInstallerSdkNewInstallFailedSrc96Evid25Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} hosting_platform - hosting_platform
 * @property {string} request_id - wait for server to get it
 * @property {string} route - URL route of the install initiator
 * @property {string} installation_id - generated for each installation
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @property {string} install_origin - location/platform/environment of the installation initiator
 * @return {LogObject<devCenterInstallerSdkNewInstallFailedSrc96Evid25Params>} LogObject<devCenterInstallerSdkNewInstallFailedSrc96Evid25Params>
 */
export function devCenterInstallerSdkNewInstallFailedSrc96Evid25(params) {
    return { evid: 25, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewInstallInitiatedSrc96Evid26' event (evid: 26)
 *
 * Event description: Client Side
 * The Consumer of the SDK launched the Installer Before Install Fire (new installer)
 *
 *
 * @param {devCenterInstallerSdkNewInstallInitiatedSrc96Evid26Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} hosting_platform - hosting_platform
 * @property {string} route - URL route of the install initiator
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @property {boolean} is_silent_install - Is the installer initiates a silent install flow
 * @property {boolean} avoid_post_installation - Is the post installation modal is skipped or not
 * @property {string} version_installed - the version of the app that is being installed on the site
 * @property {string} correlation_id - API correlation id, generated on Init
 * @property {string} install_origin - location/platform/environment of the installation initiator
 * @property {string} installation_id - generated for each installation
 * @return {LogObject<devCenterInstallerSdkNewInstallInitiatedSrc96Evid26Params>} LogObject<devCenterInstallerSdkNewInstallInitiatedSrc96Evid26Params>
 */
export function devCenterInstallerSdkNewInstallInitiatedSrc96Evid26(params) {
    return { evid: 26, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewConsentModalLoadedSrc96Evid27' event (evid: 27)
 *
 * Event description: Client Side
 * Modal loaded (new installer)
 * Exc. Error modal
 *
 *
 * @param {devCenterInstallerSdkNewConsentModalLoadedSrc96Evid27Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} hosting_platform - hosting_platform
 * @property {string} route - URL route of the install initiator
 * @property {string} install_origin - the url of the install origin
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @property {string} modal_type - The type of the modal
 * @property {string} modal_title - The title shown
 * @property {string} modal_cta_list - List of CTA buttons
 * @property {string} installation_id - installation_id
 * @return {LogObject<devCenterInstallerSdkNewConsentModalLoadedSrc96Evid27Params>} LogObject<devCenterInstallerSdkNewConsentModalLoadedSrc96Evid27Params>
 */
export function devCenterInstallerSdkNewConsentModalLoadedSrc96Evid27(params) {
    return { evid: 27, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewConsentModalCtaClickSrc96Evid28' event (evid: 28)
 *
 * Event description: Client Side
 * Modal CTA click (new installer)
 * Exc. Error modal
 *
 * @param {devCenterInstallerSdkNewConsentModalCtaClickSrc96Evid28Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} msid - msid
 * @property {string} hosting_platform - hosting_platform
 * @property {string} route - URL route of the install initiator
 * @property {string} cta_name - cta_name
 * @property {string} install_origin - the url of the install origin
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @property {string} modal_type - The type of the modal
 * @property {string} installation_id - generated for each installation
 * @return {LogObject<devCenterInstallerSdkNewConsentModalCtaClickSrc96Evid28Params>} LogObject<devCenterInstallerSdkNewConsentModalCtaClickSrc96Evid28Params>
 */
export function devCenterInstallerSdkNewConsentModalCtaClickSrc96Evid28(params) {
    return { evid: 28, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewPostConsentModalLoadedSrc96Evid30' event (evid: 30)
 *
 * Event description: Client Side
 * Post Install modal loaded (new installer)
 *
 *
 * @param {devCenterInstallerSdkNewPostConsentModalLoadedSrc96Evid30Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} account_id - for installs on account
 * @property {string} msid - msid
 * @property {string} hosting_platform - hosting_platform
 * @property {string} route - URL route of the install initiator
 * @property {string} product - container that initiate the install
 * @property {string} instance_id - instance_id
 * @property {string} install_id - install_id
 * @property {string} modal_type - modal type
 * @property {string} modal_content - List of cta
 * @property {string} install_origin - location/platform/environment of the installation initiator
 * @property {string} referral_info - referral_info
 * @return {LogObject<devCenterInstallerSdkNewPostConsentModalLoadedSrc96Evid30Params>} LogObject<devCenterInstallerSdkNewPostConsentModalLoadedSrc96Evid30Params>
 */
export function devCenterInstallerSdkNewPostConsentModalLoadedSrc96Evid30(params) {
    return { evid: 30, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewPostConsentModalCtaClickSrc96Evid39' event (evid: 39)
 *
 * Event description: Client Side
 * Post install modal CTA Click (new installer)
 *
 *
 * @param {devCenterInstallerSdkNewPostConsentModalCtaClickSrc96Evid39Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} account_id - for installs on account
 * @property {string} msid - msid
 * @property {string} hosting_platform - hosting_platform
 * @property {string} route - URL route of the install initiator
 * @property {string} product - container that initiate the install
 * @property {string} instance_id - instance_id
 * @property {string} install_id - install_id
 * @property {string} modal_type - modal type
 * @property {string} cta_name - cta_name
 * @property {string} to_url - The destination url
 * @property {string} install_origin - location/platform/environment of the installation initiator
 * @property {string} referral_info - referral_info
 * @return {LogObject<devCenterInstallerSdkNewPostConsentModalCtaClickSrc96Evid39Params>} LogObject<devCenterInstallerSdkNewPostConsentModalCtaClickSrc96Evid39Params>
 */
export function devCenterInstallerSdkNewPostConsentModalCtaClickSrc96Evid39(params) {
    return { evid: 39, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewDependencyModalLoadedSrc96Evid45' event (evid: 45)
 *
 * Event description: Client Side
 * Dependency modal loaded (new installer)
 *
 *
 * @param {devCenterInstallerSdkNewDependencyModalLoadedSrc96Evid45Params} params - The parameters for this event
 * @property {string} modal_content - List of cta
 * @return {LogObject<devCenterInstallerSdkNewDependencyModalLoadedSrc96Evid45Params>} LogObject<devCenterInstallerSdkNewDependencyModalLoadedSrc96Evid45Params>
 */
export function devCenterInstallerSdkNewDependencyModalLoadedSrc96Evid45(params) {
    return { evid: 45, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewDependencyModalCtaClickCopySrc96Evid46' event (evid: 46)
 *
 * Event description: Client Side
 * upgrade site required modal CTA Click (new installer)
 *
 * @param {devCenterInstallerSdkNewDependencyModalCtaClickCopySrc96Evid46Params} params - The parameters for this event
 * @return {LogObject<devCenterInstallerSdkNewDependencyModalCtaClickCopySrc96Evid46Params>} LogObject<devCenterInstallerSdkNewDependencyModalCtaClickCopySrc96Evid46Params>
 */
export function devCenterInstallerSdkNewDependencyModalCtaClickCopySrc96Evid46(params) {
    return { evid: 46, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewDomainRequiredModalLoadedCopySrc96Evid47' event (evid: 47)
 *
 * Event description: Client Side
 * Domain required modal loaded (new installer)
 *
 *
 * @param {devCenterInstallerSdkNewDomainRequiredModalLoadedCopySrc96Evid47Params} params - The parameters for this event
 * @property {string} modal_content - List of cta
 * @return {LogObject<devCenterInstallerSdkNewDomainRequiredModalLoadedCopySrc96Evid47Params>} LogObject<devCenterInstallerSdkNewDomainRequiredModalLoadedCopySrc96Evid47Params>
 */
export function devCenterInstallerSdkNewDomainRequiredModalLoadedCopySrc96Evid47(params) {
    return { evid: 47, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterInstallerSdkNewConnectDomainModalCtaClickSrc96Evid48' event (evid: 48)
 *
 * Event description: Client Side
 * Connect domain required modal CTA Click (new installer)
 *
 *
 * @param {devCenterInstallerSdkNewConnectDomainModalCtaClickSrc96Evid48Params} params - The parameters for this event
 * @property {string} cta_name - cta_name
 * @property {string} to_url - The destination url
 * @return {LogObject<devCenterInstallerSdkNewConnectDomainModalCtaClickSrc96Evid48Params>} LogObject<devCenterInstallerSdkNewConnectDomainModalCtaClickSrc96Evid48Params>
 */
export function devCenterInstallerSdkNewConnectDomainModalCtaClickSrc96Evid48(params) {
    return { evid: 48, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'appDashboardHomePageClickSrc96Evid49' event (evid: 49)
 *
 * Event description: Error Modal CTA click in SDK installation flow (new installer)
 *
 * @param {appDashboardHomePageClickSrc96Evid49Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} correlation_id - correlation_id generated by the consumer
 * @property {string} platform - platform param
 * @property {string} external_content_mode - Render of an external content (e.g. app page) inside the modals
 * @property {string} version - app version (undefined = latest published)
 * @property {boolean} is_silent_install - Is silent installation flow
 * @property {boolean} avoid_post_installation - is the install skip the post install redirect
 * @property {boolean} is_external_dashboard - is the install redirects to the app's external dashboard
 * @property {boolean} is_advance_oauth - Is the installation apply advance oAuth
 * @property {string} page_id - Only for Giza SDK. The current id of the page.
 * @property {string} invoker_component_id - Only for Giza SDK. The component that initiated the installer.
 * @property {string} msid - msid
 * @property {string} uuid - uuid
 * @property {string} route - The route of the hosting url
 * @property {string} installation_id - generated for each installation
 * @property {string} error_content_type - The content type of the error message
 * @property {string} error_reason - The technical reason
 * @property {string} cta_name - cta_name
 * @return {LogObject<appDashboardHomePageClickSrc96Evid49Params>} LogObject<appDashboardHomePageClickSrc96Evid49Params>
 */
export function appDashboardHomePageClickSrc96Evid49(params) {
    return { evid: 49, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterManageYourAppTeamMembersCopyLinkClick' event (evid: 90)
 *
 * Event description: Manage Your App - Team Members - Copy Link Click
 *
 * @param {devCenterManageYourAppTeamMembersCopyLinkClickParams} params - The parameters for this event
 * @property {string} app_id - app id
 * @property {string} entryPoint - the page the user copy the link from: collaborators/my apps
 * @property {string} dev_ux_type - UX flavor: developer ,no-code
 *
 * @return {LogObject<devCenterManageYourAppTeamMembersCopyLinkClickParams>} LogObject<devCenterManageYourAppTeamMembersCopyLinkClickParams>
 */
export function devCenterManageYourAppTeamMembersCopyLinkClick(params) {
    return { evid: 90, src: 96, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devCenterDashboardClickOnLearnMore' event (evid: 96)
 *
 * Event description: Click On Learn More on Any Page
 *
 * @param {devCenterDashboardClickOnLearnMoreParams} params - The parameters for this event
 * @property {string} target_url - target url of the kb
 * @property {string} loaction - components, oath, permission etc.
 * @property {string} app_id - app id
 * @property {string} location_section - specific area in the page
 * @property {string} click_name - the name of the click (learn more, etc...)
 * @property {string} dev_ux_type - ux flavor (developer, no-code)
 * @return {LogObject<devCenterDashboardClickOnLearnMoreParams>} LogObject<devCenterDashboardClickOnLearnMoreParams>
 */
export function devCenterDashboardClickOnLearnMore(params) {
    return { evid: 96, src: 96, endpoint: '', params };
}
//# sourceMappingURL=index.js.map